import React, { useEffect, useState } from 'react';

import ClipPlayer from '../../../../components/ClipPlayer';
import CreatorRow from '../../../../components/CreatorRow';
import EngagementRail from '../../../../components/EngagementRail';
import GameBadge from '../../../../components/GameBadge';
import ScrubBar from '../../../../components/ScrubBar';
import { ChevronLeftIcon, PlusIcon } from '../../../../components/icons';

import styles from './Main.module.scss';

interface MainProps {
  data: any;
}

export const getVideoQuery = (id: string) => {
  const videoDataQuery = `
    query WebCaptureQuery ($id: ID!) {
      capture (id: $id) {
        title
        creator {
          username
          givenName
          familyName
          avatarUrl
        }
        game {
          name
        }
        createdAt
        shareUrl
        hlsResourceUrl
        thumbnailUrl
        totalViewCount
      }
    }
  `;
  const variables = { id };
  return {
    query: videoDataQuery,
    variables,
  };
};

function Main({ data }: MainProps) {
  const [progress, setProgress] = useState(0);
  const [seekTo, setSeekTo] = useState<number | null>(null);

  const videoData = {
    title          : data.capture.title ?? '',
    username       : data.capture.creator?.username ? `@${data.capture.creator.username}` : '',
    game           : data.capture.game?.name ?? '',
    avatarImageUrl : data.capture.creator?.avatarUrl ?? '',
    url            : data.capture.shareUrl ?? '',
    hlsResourceUrl : data.capture.hlsResourceUrl ?? '',
    posterUrl      : data.capture.thumbnailUrl ?? '',
    viewCount      : data.capture.totalViewCount ?? 0,
  };

  // Tell iOS / Android what to show in the lock-screen and Dynamic Island
  // now-playing UI. Without this the OS falls back to the page favicon
  // (the Backbone logo).
  useEffect(() => {
    if (typeof navigator === 'undefined' || !('mediaSession' in navigator)) return;

    const artworkUrl = videoData.posterUrl;
    const artwork = artworkUrl
      ? [
          { src: artworkUrl, sizes: '512x512', type: 'image/jpeg' },
          { src: artworkUrl, sizes: '256x256', type: 'image/jpeg' },
          { src: artworkUrl, sizes: '192x192', type: 'image/jpeg' },
        ]
      : [];

    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: videoData.title || 'Backbone Clip',
      artist: videoData.username || 'Backbone',
      album: videoData.game || 'Backbone',
      artwork,
    });
  }, [
    videoData.posterUrl,
    videoData.title,
    videoData.username,
    videoData.game,
  ]);

  const handleFollow = () => {
    const username = data.capture.creator?.username;
    if (!username) return;
    window.location.href = `backbone://user/${encodeURIComponent(username)}`;
  };

  const handleSeek = (pct: number) => {
    setSeekTo(pct);
    setProgress(pct);
    // clear seekTo after consumption so repeated seeks to same pct still fire
    window.setTimeout(() => setSeekTo(null), 0);
  };

  return (
    <div className={styles.stage}>
      <ClipPlayer
        src={videoData.hlsResourceUrl}
        posterUrl={videoData.posterUrl}
        onProgress={setProgress}
        seekTo={seekTo}
      />

      <div className={styles.topChrome}>
        <button
          type="button"
          className={styles.chromeButton}
          onClick={() => window.history.length > 1 ? window.history.back() : (window.location.href = 'https://backbone.com')}
          aria-label="Back"
        >
          <ChevronLeftIcon />
        </button>
        <button
          type="button"
          className={styles.chromeButton}
          onClick={() => (window.location.href = 'https://backbone.com/app')}
          aria-label="Get the Backbone app"
        >
          <PlusIcon />
        </button>
      </div>

      <div className={styles.rightRail}>
        <EngagementRail
          shareUrl={videoData.url}
          shareTitle={videoData.title}
          likeCount={undefined}
          commentCount={undefined}
          shareCount={undefined}
        />
      </div>

      <div className={styles.bottomOverlay}>
        <CreatorRow
          username={videoData.username}
          avatarImageUrl={videoData.avatarImageUrl}
          title={videoData.title}
          onFollow={videoData.username ? handleFollow : undefined}
        />
        {videoData.game && (
          <div className={styles.gameBadgeWrap}>
            <GameBadge name={videoData.game} />
          </div>
        )}
        <div className={styles.scrubWrap}>
          <ScrubBar progress={progress} onSeek={handleSeek} />
        </div>
      </div>
    </div>
  );
}

export default Main;
